"use client";

import React from "react";
import { PieChart, Pie, Cell } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { formatCurrency } from "@/utils/formatCurrency";

type Item = { name: string; value: number };

type Props = {
  data: Item[];
  currency?: string;
  height?: number;
  filterZeros?: boolean;
  showTotal?: boolean;
  title?: string;
};

const palette = ["#3B82F6", "#F59E0B", "#EF4444", "#10B981", "#7C3AED"];

const ExitsPieChart: React.FC<Props> = ({
  data = [],
  currency = "BRL",
  height = 520,
  filterZeros = true,
  showTotal = true,
  title,
}) => {
  const filtered = React.useMemo(() => {
    return (data || []).filter((d) =>
      filterZeros ? Number(d.value) !== 0 : true,
    );
  }, [data, filterZeros]);

  const total = filtered.reduce((s, it) => s + (Number(it.value) || 0), 0);

  const chartConfig = React.useMemo(() => {
    return (filtered || []).reduce((acc: any, cur: any, idx: number) => {
      acc[cur.name] = { color: palette[idx % palette.length] };
      return acc;
    }, {});
  }, [filtered]);

  // radii based on height to make donut larger and more visible
  const outerRadius = Math.max(40, Math.floor(height / 2.1));
  const innerRadius = Math.max(18, Math.floor(height / 3.4));

  // tooltip active state to avoid overlap with center total
  const [tooltipActive, setTooltipActive] = React.useState(false);

  const TooltipWrapper: React.FC<any> = (props) => {
    React.useEffect(() => {
      setTooltipActive(Boolean(props?.active));
    }, [props?.active]);

    return <ChartTooltipContent {...props} hideLabel nameKey="name" />;
  };

  // Legend built from the filtered data (show name and formatted value)
  const LegendWithValues: React.FC = () => {
    const items = React.useMemo(() => {
      return (filtered || [])
        .slice()
        .sort((a: any, b: any) => Number(b.value) - Number(a.value));
    }, [filtered]);

    if (!items || items.length === 0) return null;

    return (
      <div className="flex flex-col items-start gap-3 mt-4 w-full px-4">
        {items.map((it: any, idx: number) => (
          <div
            key={`legend-${idx}`}
            className="flex items-center gap-3 text-sm text-gray-700 w-full"
          >
            <span
              className="h-3 w-3 rounded"
              style={{
                backgroundColor:
                  chartConfig?.[it.name]?.color ||
                  palette[idx % palette.length],
              }}
            />
            <span className="flex-1 truncate">{it.name}</span>
            <span className="font-semibold ml-2">
              {formatCurrency(Number(it.value) || 0, currency)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (!filtered || filtered.length === 0) {
    return (
      <div className="text-sm text-gray-500">Nenhuma saída encontrada</div>
    );
  }

  return (
    <div className="flex w-full flex-col items-stretch justify-start">
      <div
        className="relative flex flex-1 items-center justify-center"
        style={{ minHeight: height }}
      >
        <ChartContainer
          config={chartConfig}
          className="aspect-square h-full w-full max-h-full items-center justify-center"
        >
          <PieChart>
            <ChartTooltip cursor={false} content={<TooltipWrapper />} />

            <Pie
              data={filtered}
              dataKey="value"
              nameKey="name"
              height={height}
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              paddingAngle={4}
              labelLine={false}
              isAnimationActive={false}
              cx="50%"
              cy="50%"
            >
              {filtered.map((entry: any, idx: number) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={
                    chartConfig?.[entry.name]?.color ||
                    palette[idx % palette.length]
                  }
                  stroke="#ffffff"
                  strokeWidth={1}
                />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>

        {showTotal && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-150"
            style={{ opacity: tooltipActive ? 0 : 1 }}
            aria-hidden={tooltipActive}
          >
            <div className="text-center">
              <div className="text-sm text-muted-foreground">{title}</div>
              <div className="text-2xl font-semibold">
                {formatCurrency(total, currency)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Render legend from data to ensure correct values and spacing */}
      <LegendWithValues />
    </div>
  );
};

export default ExitsPieChart;
